import { NSARouterMakerResult } from "namirasoft-account-react";
import { TemplateGenerator, TemplateRow } from "namirasoft-password";
import { BrowserDetector, LinkOperation, LinkTarget, NSMenuActionProps } from "namirasoft-site-react";
import { PasswordExtensionLink } from "./PasswordExtensionLink";
import { Storage } from "./Storage";

export class PasswordMenus
{
    static download(): NSMenuActionProps
    {
        const browser = BrowserDetector.detect();
        const link = PasswordExtensionLink[browser];

        return {
            id: "1",
            menu_item: "Download Extension",
            isActive: () => !!link,
            onClick: {
                showLoading: false,
                action: () =>
                {
                    if (link)
                        LinkOperation.open(link, LinkTarget.Blank);
                },
            }
        }
    }

    static generate(props: NSARouterMakerResult, getRows: () => TemplateRow[]): NSMenuActionProps
    {
        return {
            id: "2",
            menu_item: "Generate",
            isActive: () => getRows().length === 1,
            onClick: {
                showLoading: false,
                action: () =>
                {
                    let row = getRows()[0];
                    let generated = TemplateGenerator.generate(row);
                    navigator.clipboard.writeText(generated);
                    props.notifier.onSuccess("Password copied to clipboard.");
                }
            }
        }
    }

    // Clears the cached PRF key. The stored ciphertext stays, so the next
    // operation only needs a passkey prompt — not the master password.
    static lock(props: NSARouterMakerResult): NSMenuActionProps
    {
        return {
            id: "password-lock",
            menu_item: "Lock Vault",
            isActive: () => Storage.isUnlocked(),
            onClick: {
                showLoading: false,
                action: () =>
                {
                    Storage.lock();
                    props.notifier.onSuccess("Vault locked.");
                    window.location.reload();
                },
            }
        }
    }

    // Forces a fresh passkey ceremony to re-prime the cache without
    // re-entering the master password.
    static unlock(props: NSARouterMakerResult): NSMenuActionProps
    {
        return {
            id: "password-unlock",
            menu_item: "Unlock Vault",
            isActive: () => Storage.isPasskeyRegistered() && !Storage.isUnlocked(),
            onClick: {
                showLoading: true,
                action: async (onFinished) =>
                {
                    try
                    {
                        await Storage.unlockPasskey();
                        props.notifier.onSuccess("Vault unlocked.");
                        window.location.reload();
                    }
                    catch (error)
                    {
                        props.notifier.onError(error as Error);
                    }
                    finally
                    {
                        onFinished();
                    }
                },
            }
        }
    }

    // Wipes the passkey credential, all encrypted blobs, and the cached key.
    // The user must re-register a passkey and re-enter the master password.
    // The underlying passkey still lives on the authenticator — remove it from
    // OS passkey settings if a full purge is wanted.
    static resetPasskey(props: NSARouterMakerResult): NSMenuActionProps
    {
        return {
            id: "password-reset-passkey",
            menu_item: "Reset Passkey",
            isActive: () => Storage.isPasskeyRegistered(),
            onClick: {
                showLoading: false,
                action: () =>
                {
                    Storage.resetPasskey();
                    props.notifier.onSuccess("Passkey reset. You will need to re-enter your master password.");
                    window.location.reload();
                },
            }
        }
    }

    static passwordGroup(props: NSARouterMakerResult): NSMenuActionProps[]
    {
        return [
            PasswordMenus.lock(props),
            PasswordMenus.unlock(props),
            PasswordMenus.resetPasskey(props),
        ];
    }
}